import { ALL_LOCATIONS, PROVINCES } from '@/app/lib/constants'

/**
 * Alias para etiquetas `province` irregulares del backend que no matchean por
 * label normalizado contra `PROVINCES` (ver spec.md, Clarifications). Claves
 * ya normalizadas (minúsculas, sin acentos, espacios colapsados).
 */
const PROVINCE_LABEL_ALIASES: Readonly<Record<string, string>> = {
  'palmas (las)': 'palmas',
  'valencia/valencia': 'valencia',
  araba: 'alava',
}

const NOT_DEFINED_LABEL = 'not defined'

/** Minúsculas, sin acentos, espacios colapsados — para comparar por label, no para mostrar. */
function normalizeLabel(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
}

const SLUG_BY_NORMALIZED_LABEL: ReadonlyMap<string, string> = new Map(
  PROVINCES.map((province) => [normalizeLabel(province.label), province.slug]),
)

const LABEL_BY_SLUG: ReadonlyMap<string, string> = new Map(
  PROVINCES.map((province) => [province.slug, province.label]),
)

const ALL_LOCATIONS_LABEL = LABEL_BY_SLUG.get(ALL_LOCATIONS) ?? ALL_LOCATIONS

/**
 * Resuelve el `province` libre del backend (p. ej. `"Palmas (Las)"`,
 * `"Not Defined"`, `null`) al slug canónico de `PROVINCES`. `null`,
 * `"Not Defined"` o cualquier etiqueta no mapeable devuelven `ALL_LOCATIONS`
 * (`'todos'`) — ver research.md §8, FR-017.
 */
export function mapProvinceToSlug(province: string | null): string {
  if (!province) return ALL_LOCATIONS

  const normalized = normalizeLabel(province)
  if (normalized === NOT_DEFINED_LABEL) return ALL_LOCATIONS

  const aliasSlug = PROVINCE_LABEL_ALIASES[normalized]
  if (aliasSlug) return aliasSlug

  return SLUG_BY_NORMALIZED_LABEL.get(normalized) ?? ALL_LOCATIONS
}

/**
 * Resuelve el slug de provincia de la URL (p. ej. `"madrid"`) a su label
 * legible de `PROVINCES` (p. ej. `"Madrid"`), para interpolar en textos
 * editoriales (`%{province}`, ver specs/029-seo-categoria-subcategoria).
 * Sin argumento, o slug sin match en `PROVINCES`, devuelve el label de
 * `ALL_LOCATIONS` ("España (todas)").
 */
export function resolveProvinceLabel(locationSlug?: string): string {
  if (!locationSlug) return ALL_LOCATIONS_LABEL
  return LABEL_BY_SLUG.get(locationSlug) ?? ALL_LOCATIONS_LABEL
}
